import React, { useState, useEffect, useRef, useCallback } from 'react';
import './styles.scss';

export type FilterType = 'greaterThan' | 'lessThan' | 'inRange';

export interface DurationFilterValue {
  type: FilterType;
  fromMinutes: number;
  toMinutes?: number;
}

interface DurationFilterComponentProps {
  onApply: (filterValue: DurationFilterValue | null) => void;
  onReset?: () => void;
  onClose?: () => void;
  currentFilter?: DurationFilterValue | null;
}

const DurationFilterComponent: React.FC<DurationFilterComponentProps> = ({
  onApply,
  onReset,
  onClose,
  currentFilter,
}) => {
  const componentRef = useRef<HTMLDivElement>(null);

  // Initialize state with current filter or defaults
  const [filterType, setFilterType] = useState<FilterType>(
    currentFilter?.type || 'greaterThan'
  );

  const [fromMinutes, setFromMinutes] = useState<string>(
    currentFilter?.fromMinutes?.toString() || ''
  );

  const [toMinutes, setToMinutes] = useState<string>(
    currentFilter?.toMinutes?.toString() || ''
  );

  // Track if filter has been applied
  const [hasAppliedFilter, setHasAppliedFilter] = useState<boolean>(
    !!currentFilter
  );

  // Track temporary changes (before Apply is clicked)
  const [tempChanges, setTempChanges] = useState({
    filterType: currentFilter?.type || 'greaterThan',
    fromMinutes: currentFilter?.fromMinutes?.toString() || '',
    toMinutes: currentFilter?.toMinutes?.toString() || '',
  });

  // Sync state when currentFilter prop changes
  useEffect(() => {
    if (currentFilter) {
      setFilterType(currentFilter.type);
      setFromMinutes(currentFilter.fromMinutes.toString());
      setToMinutes(currentFilter.toMinutes?.toString() || '');
      setHasAppliedFilter(true);
      setTempChanges({
        filterType: currentFilter.type,
        fromMinutes: currentFilter.fromMinutes.toString(),
        toMinutes: currentFilter.toMinutes?.toString() || '',
      });
    } else {
      // Reset to defaults
      setFilterType('greaterThan');
      setFromMinutes('');
      setToMinutes('');
      setHasAppliedFilter(false);
      setTempChanges({
        filterType: 'greaterThan',
        fromMinutes: '',
        toMinutes: '',
      });
    }
  }, [currentFilter]);

  // Check if current values match defaults
  const isUsingDefaultValues = useCallback((): boolean => {
    return filterType === 'greaterThan' && fromMinutes === '';
  }, [filterType, fromMinutes]);

  // Check if current values match saved values
  const doesCurrentMatchSaved = useCallback((): boolean => {
    const filterTypeMatches = filterType === tempChanges.filterType;
    const fromMatches = fromMinutes === tempChanges.fromMinutes;
    const toMatches = toMinutes === tempChanges.toMinutes;
    const isBetweenMode = filterType === 'inRange';
    const toMatchesOrNotRequired = !isBetweenMode || toMatches;

    return filterTypeMatches && fromMatches && toMatchesOrNotRequired;
  }, [filterType, fromMinutes, toMinutes, tempChanges]);

  // Check if there are unsaved changes
  const hasChanges = useCallback((): boolean => {
    return hasAppliedFilter
      ? !doesCurrentMatchSaved()
      : !isUsingDefaultValues();
  }, [hasAppliedFilter, isUsingDefaultValues, doesCurrentMatchSaved]);

  // Helper function to revert to the last saved state
  const revertToSavedState = useCallback(() => {
    setFilterType(tempChanges.filterType);
    setFromMinutes(tempChanges.fromMinutes);
    setToMinutes(tempChanges.toMinutes);
  }, [tempChanges]);

  // Handle clicking outside the component
  useEffect(() => {
    const isProtectedElement = (target: HTMLElement): boolean => {
      return !!target.closest('.ag-menu') || !!target.closest('.ag-popup');
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (isProtectedElement(target)) {
        return;
      }

      setTimeout(() => {
        const isClickOutsideComponent =
          !componentRef.current?.contains(target) &&
          !isProtectedElement(target);

        if (isClickOutsideComponent) {
          if (hasChanges()) {
            revertToSavedState();
          }
          onClose?.();
        }
      }, 50);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [hasChanges, onClose, revertToSavedState]);

  // === Event Handlers ===

  const handleFilterTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterType(e.target.value as FilterType);
  };

  const handleFromMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const isValidNumericInput = value === '' || /^\d+$/.test(value);

    if (isValidNumericInput) {
      setFromMinutes(value);
    }
  };

  const handleToMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const isValidNumericInput = value === '' || /^\d+$/.test(value);

    if (isValidNumericInput) {
      setToMinutes(value);
    }
  };

  const handleCancel = () => {
    revertToSavedState();
    onClose?.();
  };

  const handleReset = () => {
    setFilterType('greaterThan');
    setFromMinutes('');
    setToMinutes('');
    setTempChanges({
      filterType: 'greaterThan',
      fromMinutes: '',
      toMinutes: '',
    });
    setHasAppliedFilter(false);

    if (onReset) {
      onReset();
    }
    onClose?.();
  };

  const handleApply = () => {
    const fromValue = parseInt(fromMinutes, 10);
    const isFromValueInvalid = isNaN(fromValue) || fromValue < 0;

    if (isFromValueInvalid) {
      return;
    }

    const isBetweenFilter = filterType === 'inRange';
    let filterValue: DurationFilterValue;

    if (isBetweenFilter) {
      const toValue = parseInt(toMinutes, 10);
      const isToValueInvalid = isNaN(toValue) || toValue < 0;

      if (isToValueInvalid) {
        return;
      }
      filterValue = {
        type: filterType,
        fromMinutes: fromValue,
        toMinutes: toValue,
      };
    } else {
      filterValue = {
        type: filterType,
        fromMinutes: fromValue,
      };
    }

    setTempChanges({
      filterType,
      fromMinutes,
      toMinutes,
    });
    setHasAppliedFilter(true);
    onApply(filterValue);
    onClose?.();
  };

  // Check if Apply button should be disabled
  const isApplyDisabled = (): boolean => {
    const fromValue = parseInt(fromMinutes, 10);
    const isFromValueInvalid =
      fromMinutes === '' || isNaN(fromValue) || fromValue < 0;

    if (isFromValueInvalid) {
      return true;
    }

    if (filterType === 'inRange') {
      const toValue = parseInt(toMinutes, 10);
      const isToValueInvalid =
        toMinutes === '' || isNaN(toValue) || toValue < 0;

      if (isToValueInvalid) {
        return true;
      }
    }

    return false;
  };

  const getPlaceholder = (isFrom: boolean): string => {
    if (filterType === 'inRange') {
      return isFrom ? 'Enter from # minutes' : 'Enter to # minutes';
    }
    return 'Enter # minutes';
  };

  return (
    <div className="duration-filter-component" ref={componentRef}>
      <div className="filter-content">
        {/* Filter Type Selector */}
        <div className="filter-row">
          <select
            id="filter-type"
            value={filterType}
            onChange={handleFilterTypeChange}
            className="filter-select"
          >
            <option value="greaterThan">Greater Than</option>
            <option value="lessThan">Less Than</option>
            <option value="inRange">In Range</option>
          </select>
        </div>

        {/* From Minutes Input */}
        <div className="input-section">
          <div className="input-row">
            <input
              type="text"
              value={fromMinutes}
              onChange={handleFromMinutesChange}
              placeholder={getPlaceholder(true)}
              className="filter-input"
            />
          </div>
        </div>

        {/* To Minutes Input (only for 'inRange' mode) */}
        {filterType === 'inRange' && (
          <div className="input-section">
            <div className="input-row">
              <input
                type="text"
                value={toMinutes}
                onChange={handleToMinutesChange}
                placeholder={getPlaceholder(false)}
                className="filter-input"
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="button-row">
          <button className="btn btn-cancel" onClick={handleCancel}>
            Cancel
          </button>

          <button
            className="btn btn-reset"
            onClick={handleReset}
            disabled={!hasAppliedFilter && isUsingDefaultValues()}
            title={
              !hasAppliedFilter && isUsingDefaultValues()
                ? 'No filter to reset'
                : 'Reset to defaults'
            }
          >
            Reset
          </button>

          <button
            className="btn btn-apply"
            onClick={handleApply}
            disabled={isApplyDisabled()}
            title="Apply filter"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default DurationFilterComponent;
